import { assertNever } from "@impero-com/common/utils/obj";
import { Divider } from "@impero-com/ui";

import type {
    ControlConclusionAnswerKind,
    SampleConclusionAnswerKind,
    SavedBlob,
} from "api/generated/model";

import { HeadingTask } from "./HeadingTask";
import { ReadOnlyTaskField } from "./ReadOnlyTaskField";
import { TaskWrapper } from "./TaskWrapper";
import { ReadOnlyTaskWithAnswer, TasksField, TasksFields } from "./types";
import { getVisibleFields } from "./utils";

interface ReadOnlyTasksProps {
    fields: TasksFields;
    answers: Record<
        string,
        ControlConclusionAnswerKind | SampleConclusionAnswerKind
    >;
    savedBlobs: SavedBlob[];
}
export function ReadOnlyTasks({
    fields,
    answers,
    savedBlobs,
}: ReadOnlyTasksProps) {
    const maybeGetFieldValue = (id: string) => answers[id]?.value;
    const visibleFields = getVisibleFields(fields, maybeGetFieldValue);

    return (
        <>
            {visibleFields.flatMap((field, idx) => {
                const reactField = (
                    <Field
                        key={field.id}
                        answers={answers}
                        field={field}
                        savedBlobs={savedBlobs}
                    />
                );
                return idx === 0
                    ? reactField
                    : [<Divider key={`divider-${field.id}`} />, reactField];
            })}
        </>
    );
}

interface FieldProps {
    field: TasksField;
    answers: Record<
        string,
        ControlConclusionAnswerKind | SampleConclusionAnswerKind
    >;
    savedBlobs: SavedBlob[];
}
function Field({ field, answers, savedBlobs }: FieldProps) {
    switch (field.fieldType.type) {
        case "CheckboxGroup":
        case "Dropdown":
        case "NumericInput":
        case "FileUpload":
        case "RadioButtonGroup":
        case "TextArea":
        case "TextInput":
        case "Effectiveness":
            return (
                <TaskWrapper field={field}>
                    <ReadOnlyTaskField
                        field={
                            {
                                type: field.fieldType.type,
                                field: field.fieldType,
                                value: answers?.[field.id]?.value,
                            } as ReadOnlyTaskWithAnswer
                        }
                        savedBlobs={savedBlobs}
                    />
                </TaskWrapper>
            );
        case "Heading":
            return <HeadingTask field={field} />;
        default:
            return assertNever(field.fieldType);
    }
}
